import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Board } from '@kaba/shared';
import { api } from './api';
import { toast } from './toast';

/* ---------- Zuletzt geöffnete Boards (dieses Gerät, je Konto getrennt – B04/A07) ---------- */

const RECENT_MAX = 8;
let account: string | null = null;

/** Angemeldetes Konto: persönliche Geräte-Einstellungen gelten nur dafür (beim Kontowechsel keine fremden Einträge) */
export function setAccount(id: string | null) {
  account = id;
  try {
    // Alter, kontoübergreifender Schlüssel aus Versionen vor 0.25
    localStorage.removeItem('kaba.recentBoards');
  } catch {
    /* privater Modus */
  }
}

const recentKey = () => (account ? `kaba.recentBoards.${account}` : null);

export function recentBoardIds(): string[] {
  const key = recentKey();
  if (!key) return [];
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function noteBoardOpened(id: string) {
  const key = recentKey();
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify([id, ...recentBoardIds().filter((x) => x !== id)].slice(0, RECENT_MAX)));
  } catch {
    /* privater Modus */
  }
}

/** Persönliche Einstellung je Konto auf diesem Gerät (Ort, Liste/Kacheln, eingeklappte Bereiche) */
export function accountPref<T>(name: string, fallback: T): T {
  if (!account) return fallback;
  try {
    const v = localStorage.getItem(`kaba.${name}.${account}`);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}

export function setAccountPref(name: string, value: unknown) {
  if (!account) return;
  try {
    localStorage.setItem(`kaba.${name}.${account}`, JSON.stringify(value));
  } catch {
    /* egal */
  }
}

/* ---------- Favoriten (am Konto, für alle Geräte) ---------- */

export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) => api.put(`/api/boards/${id}/favorite`, { favorite }),
    onMutate: ({ id, favorite }) => {
      const before = qc.getQueryData<Board[]>(['boards']);
      qc.setQueryData<Board[]>(['boards'], (list) => list?.map((b) => (b.id === id ? { ...b, favorite } : b)));
      return { before };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.before) qc.setQueryData(['boards'], ctx.before);
      toast.error(`Favorit nicht gespeichert: ${(err as Error).message}`);
    },
  });
}

/* ---------- Ansicht je Board und Geräteklasse ---------- */

export type View = 'board' | 'list' | 'table' | 'calendar' | 'timeline' | 'files' | 'overview' | 'metrics';
export const VIEWS: View[] = ['board', 'list', 'table', 'calendar', 'timeline', 'files', 'overview', 'metrics'];
export type DeviceClass = 'phone' | 'desktop';

let device: DeviceClass | null = null;
/**
 * Geräteklasse einmal beim Laden festlegen. Ein schmaleres oder breiteres Fenster wechselt die
 * Ansicht danach nicht mehr – das Layout passt sich an, die gewählte Ansicht bleibt.
 */
export function deviceClass(): DeviceClass {
  device ??= window.matchMedia('(max-width: 639px)').matches ? 'phone' : 'desktop';
  return device;
}

const viewKey = (boardId: string, d: DeviceClass) => `kaba.view.${boardId}.${d}`;
const lastKey = (d: DeviceClass) => `kaba.view.last.${d}`;
const isView = (v: unknown): v is View => typeof v === 'string' && (VIEWS as string[]).includes(v);

/** Gemerkte Ansicht dieses Boards auf dieser Geräteklasse; sonst die zuletzt genutzte; sonst Board bzw. Liste */
export function preferredView(boardId: string): View {
  const d = deviceClass();
  try {
    const own = localStorage.getItem(viewKey(boardId, d));
    if (isView(own)) return own;
    const last = localStorage.getItem(lastKey(d)) ?? (d === 'desktop' ? localStorage.getItem('kaba.view') : null);
    if (last === 'board' || last === 'list') return last;
  } catch {
    /* privater Modus */
  }
  return d === 'phone' ? 'list' : 'board';
}

export function rememberView(boardId: string, v: View) {
  const d = deviceClass();
  try {
    localStorage.setItem(viewKey(boardId, d), v);
    localStorage.setItem(lastKey(d), v);
  } catch {
    /* egal */
  }
}
